/**
 * 부정발주(짜고치기·재발주) 케이스 모달 — $j.F.Admin.Collusion.Case
 *
 * **화면에 매이지 않는다.** 케이스 번호 하나만 있으면 어디서든 판정창을 연다 —
 * 제재관리(`/adm/member/penalty` 짜고치기·재발주 탭)와 알림 모아보기(`/adm/tasks`)가 같이 쓴다.
 *
 *   $j.F.Admin.Collusion.Case.config({ cfg, onChanged: reload });   // 화면 1회
 *   $j.F.Admin.Collusion.Case.open(caseNum);                        // 열기
 *
 * **판정 한 번으로 벌이 끝난다** — 벌의 모양이 패턴마다 정해져 있어 화면을 옮길 이유가 없다.
 *   짜고치기 — 짜고친 관계가 부정이다 → 참여자 선택창에서 수발주제한 (한 패 단위)
 *   재발주 — 혼자서 -6차를 노렸다 → 그 -6차를 회수 (보유분 없으면 회수예약)
 *
 * 모양(.co-*)은 `admin/pages/_collusion.scss` 의 `#penRoot` **밖** 블록이라 전 관리자 화면에
 * 이미 실려 있다. 모달은 body 밑에 붙으므로 그래야 한다.
 *
 * 목록칸 렌더(memberCell·statusCell·scoreCls…)는 제재관리 표도 같은 것을 쓴다 —
 * 두 벌로 두면 같은 케이스인데 표와 모달이 다른 말을 한다.
 */
import Util from '../../../Common/Util';
export default class Case {

    /**
     * 화면이 한 번 부른다.
     * @param {object} opts cfg: 배점표(상태 라벨 등, summary 응답) · onChanged: 판정 뒤 목록 갱신
     */
    config(opts = {}) {
        if (opts.cfg !== undefined) this.cfg = opts.cfg;
        if (opts.onChanged) this.onChanged = opts.onChanged;
        this.bindActions();
        return this;
    }

    /** 판정으로 원장이 바뀌었다 — 부른 쪽이 목록을 다시 받게 한다 */
    changed() {
        if (this.onChanged) this.onChanged();
    }

    /** 원장에서 이 종류를 부르는 이름 — lookup 이 쓴다 */
    static TASK_TYPE = 'collusion';

    /**
     * 케이스를 연다.
     *
     * **열기 전에 원장을 본다.** 담당·처리 상태는 원장에만 있어서, 남이 붙잡고 있는 건을
     * 모르고 겹쳐 처리하는 것을 여기서 막는다. 막지는 않고 한 번 묻는다 — 휴가·퇴근으로
     * 넘겨받는 일이 실제로 있다.
     *
     * 담당이 비어 있으면 조용히 맡는다. **여는 것이 곧 "내가 본다"** 이고, 그래야 담당이
     * 형식으로 남지 않는다. 모달 한 곳에 두었으므로 제재관리·알림 모아보기 양쪽이 같이 얻는다.
     */
    async open(caseNum) {
        // 여는 순간 담당이 붙으면(맡기·넘겨받기) 목록도 다시 받아야 한다 — 안 그러면
        // 창을 닫아도 목록엔 '미배정' 이 남는다
        if (!await $j.F.Admin.TaskGuard.check(Case.TASK_TYPE, caseNum,
                { onClaimed: () => this.changed() })) return;

        try {
            const r = await $.ajax({ url: `/adm/ajax/collusion/${caseNum}/detail`, type: 'get', dataType: 'json' });
            if (!r.ok) { Util.swalert(r.message || '케이스를 불러오지 못했습니다.'); return; }

            const c = r.case;
            const isPair = c.cs_type === 'pair';
            const title = isPair ? '짜고치기' : '재발주';

            $j.Ui.JBModal.html('collusion-case-modal', this.caseBody(r),
                `${title} 케이스 #${c.num} — 의심도 ${c.score}점`, { width: '900px' });
            this.current = r;

            // 머리줄의 공통 칩 — 담당·상태·이력. 창마다 본문이 달라도 이 자리는 셋이 같다
            $j.F.Admin.TaskBadge.attach($('#collusion-case-modal'), Case.TASK_TYPE, caseNum,
                { onChanged: () => this.changed() });
        } catch (e) {
            console.error('케이스 상세 로드 실패', e);
            Util.swalert('케이스를 불러오지 못했습니다.');
        }
    }

    caseBody(r) {
        const c = r.case;
        const d = c.detail || {};
        const isPair = c.cs_type === 'pair';

        const partRows = (d.parts || []).map(p => `
            <div class="co-part ${p.score > 0 ? '' : 'is-zero'}">
                <span class="co-part-score">+${p.score}</span>
                <span class="co-part-label">${this.esc(p.label)}</span>
            </div>`).join('');

        const orders = (r.orders || []).map(o => `
            <tr>
                <td class="text-center"><a href="javascript:;" class="co-order" data-o="${o.ordernum}">${o.ordernum}</a></td>
                <td class="text-center">${this.roleBadge(o.role)}</td>
                <td class="text-center">${o.work_day}</td>
                <td>${this.esc(o.site_address || '')}</td>
                <td class="text-center">${this.esc(o.car_type || '')}</td>
                <td class="text-right">${this.n(o.car_price)}</td>
                <td>${$j.F.Admin.Member.Pop.cell(o.order_owner_num, o.bal_name)} <i class="fas fa-arrow-right text-muted"></i> ${$j.F.Admin.Member.Pop.cell(o.worker_owner_num, o.soo_name)}</td>
            </tr>`).join('');

        const ring = (r.ring || []).length ? `
            <div class="co-sec">
                <h6><i class="fas fa-project-diagram"></i> 같은 패의 다른 쌍</h6>
                ${r.ring.map(x => `<div class="co-line">
                        <span class="co-score ${this.scoreCls(x.score)}">${x.score}</span>
                        ${$j.F.Admin.Member.Pop.cell(x.mb_a, x.a_name)} <i class="fas fa-arrow-right text-muted"></i> ${$j.F.Admin.Member.Pop.cell(x.mb_b, x.b_name)}
                        <span class="text-muted">거래 ${x.deal_cnt}건 · -6차 ${x.msix_cnt}건</span>
                        <a href="javascript:;" class="co-goto ml-2" data-case="${x.num}">보기</a>
                    </div>`).join('')}
            </div>` : '';

        const others = (r.others || []).length ? `
            <div class="co-sec">
                <h6><i class="fas fa-history"></i> 당사자가 걸린 다른 케이스 ${r.others.length}건</h6>
                ${r.others.map(x => `<div class="co-line">
                        <span class="co-score ${this.scoreCls(x.score)}">${x.score}</span>
                        <span class="co-type">${x.cs_type === 'pair' ? '짜고치기' : '재발주'}</span>
                        <span class="text-muted">${x.last_day || ''} · 거래 ${x.deal_cnt}건 · ${this.statusText(x.status)}</span>
                        <a href="javascript:;" class="co-goto ml-2" data-case="${x.num}">보기</a>
                    </div>`).join('')}
            </div>` : '';

        // 순서: 당사자 → 점수 내역 → 같은 패·다른 케이스 → **판정** → 근거 오더.
        // 근거 오더 표가 길어(13건짜리도 있다) 위에 두면 사유칸과 버튼이 화면 밖으로 밀린다.
        // 판정은 늘 보이게 두고, 근거는 필요할 때 아래로 스크롤해 본다.
        const special = d.special
            ? `<div class="alert alert-info py-2 mb-2" style="font-size:12.5px">
                   <b>특차로 등록된 쌍</b>입니다 — CS 가 승인한 거래관계라 반복이 정상일 수 있습니다.</div>` : '';

        return `
        <div class="co-case">
            ${special}
            <div class="co-case-head">
                <div class="co-party">
                    <div class="co-party-role">${isPair ? '발주자' : '재발주한 회원'}</div>
                    ${$j.F.Admin.Member.Pop.cell(c.mb_a, c.a_name)}
                    <div class="text-muted">${this.esc(c.a_sido || '')} ${this.esc(c.a_phone || '')}</div>
                </div>
                <i class="fas fa-exchange-alt co-party-arrow"></i>
                <div class="co-party">
                    <div class="co-party-role">${isPair ? '수주자' : '재발주를 잡은 회원'}</div>
                    ${c.mb_b > 0
                        ? `${$j.F.Admin.Member.Pop.cell(c.mb_b, c.b_name)}
                           <div class="text-muted">${this.esc(c.b_sido || '')} ${this.esc(c.b_phone || '')}</div>`
                        : '<span class="text-muted">미수주</span>'}
                </div>
                <div class="co-case-sum">
                    <div><b>${c.deal_cnt}</b>건 거래</div>
                    <div><i class="fas fa-bolt res-i is-msix"></i> <b>${c.msix_cnt}</b>건 취득</div>
                    <div>${this.n(c.amount)}원</div>
                    <div class="text-muted">${c.first_day} ~ ${c.last_day}</div>
                </div>
            </div>

            <div class="co-sec">
                <h6><i class="fas fa-calculator"></i> 점수 내역</h6>
                ${partRows}
            </div>

            ${ring}
            ${others}

            <div class="co-sec co-judge">
                <h6><i class="fas fa-gavel"></i> 판정</h6>
                <div class="text-muted mb-2" style="font-size:12.5px">
                    현재 상태 ${this.statusBadge(c)}
                    ${c.done_name ? ` · ${this.esc(c.done_name)} · ${(c.done_at || '').slice(0, 16)}` : ''}
                    ${!c.done_name && c.assignee_name ? ` · ${this.esc(c.assignee_name)} 담당` : ''}
                </div>
                ${isPair ? '' : this.msixBox(r, c)}
                ${this.judgeBox(c, isPair)}
            </div>

            <div class="co-sec co-orders">
                <h6><i class="fas fa-list"></i> 근거 오더 ${(r.orders || []).length}건 <small class="text-muted">— 오더번호를 누르면 핸들링 창</small>
                    ${this.ordersLink(r.orders)}</h6>
                <table class="table table-sm co-mini">
                    <thead><tr><th>오더</th><th>역할</th><th>작업일</th><th>현장</th><th>기종</th>
                               <th>금액</th><th>발주 → 수주</th></tr></thead>
                    <tbody>${orders || '<tr><td colspan="7" class="text-center text-muted">근거 오더가 없습니다.</td></tr>'}</tbody>
                </table>
            </div>
        </div>`;
    }

    /**
     * 회수 대상 -6차 — **판정 버튼 바로 위에 둔다.**
     *
     * 무엇을 뺏게 되는지 모르고 누르는 버튼이 되지 않게, 상태를 그대로 보인다. 이미 써버린
     * 건은 회수할 대상이 없어 예약(앞으로 받을 -6차부터 차감)으로 돌아가는데, 그 갈림길이
     * 눌러본 뒤에 밝혀지면 CS 는 자기가 무엇을 했는지 모른다.
     */

    /**
     * 판정칸 — **끝난 건은 읽기만, 고치려면 되돌린다.**
     *
     * 판정이 난 케이스(정상·제재)를 다시 열어보는 일이 잦다 — "무엇을 보고 그렇게 판정했나".
     * 그때 입력칸과 판정 버튼이 그대로 서 있으면 두 가지가 잘못된다: 이미 집행된 벌을 또
     * 누를 수 있는 것처럼 보이고(회수는 되돌릴 수 없다), 화면이 '아직 안 끝난 일' 로 읽힌다.
     * 다시 판정해야 하면 상태를 미판정으로 되돌리고 들어온다 — 그게 기록에도 남는다 → reopen()
     *
     * 되돌리기는 **정상 판정 건에만** 세운다. 제재는 수발주제한·-6차 회수가 이미 집행돼 있어
     * 케이스 상태만 되돌리면 제재원장과 어긋난다 — 그쪽은 「제재 목록」에서 해제·수정한다.
     */
    judgeBox(c, isPair) {
        const done = c.status === 'clear' || c.status === 'punish';

        if (done) {
            const memo = $.trim(c.memo || '');
            const read = `<div class="co-judge__memo">${memo ? this.esc(memo) : '<span class="text-muted">판정 사유가 적혀 있지 않습니다.</span>'}</div>`;

            return read + (c.status === 'clear'
                ? `<div class="mt-2 text-right">
                       <button type="button" class="btn btn-sm btn-outline-secondary" id="coReopen" data-case="${c.num}">
                           <i class="fas fa-undo"></i> 미판정으로 되돌리기
                       </button>
                   </div>`
                : `<div class="mt-2 text-right text-muted" style="font-size:12.5px">
                       제재로 닫힌 케이스입니다 — 판정을 고치려면 「제재 목록」 탭에서 그 제재를 해제·수정하세요.
                   </div>`);
        }

        return `
            <textarea class="form-control" id="coMemo" rows="2"
                      placeholder="판정 사유 (정상 판정·제재에는 필수)">${this.esc(c.memo || '')}</textarea>
            <div class="mt-2 text-right">
                <button type="button" class="btn btn-sm btn-outline-primary" data-judge="clear" data-case="${c.num}">정상으로 판정</button>
                ${isPair
                    ? `<button type="button" class="btn btn-sm btn-danger" id="coPunish" data-case="${c.num}">
                           <i class="fas fa-ban"></i> 짜고치기 제재 등록
                       </button>`
                    // 재발주는 공범이 없다 — 노린 것이 -6차라 벌도 그것을 되가져오는 것이다.
                    // 회수 대상은 케이스가 이미 알고 있어서(msixGrants) 이 창에서 끝난다.
                    : `<button type="button" class="btn btn-sm btn-danger" id="coPunishOne" data-case="${c.num}">
                           <i class="fas fa-undo-alt"></i> -6차 회수 제재
                       </button>`}
            </div>`;
    }

    /**
     * 회수 대상 -6차. 예약이 걸리는 건이 있으면 **그 예약을 어디서 보는지**까지 적는다 —
     * 예약은 지금 뺏는 것이 아니라 앞으로 받을 것에서 차감하는 약속이라, "정말 걸렸나" 를
     * 확인할 자리가 필요하다. 그 자리는 「-6차 회수」탭이다.
     */
    msixBox(r, c = null) {
        const list  = r.msix || [];
        const total = parseInt(r.msixOrders) || 0;

        // 같은 상자가 판정 전(예고)과 제재 후(결과) 양쪽에 뜬다 — 시제를 갈라 적지 않으면
        // "회수예약" 이 이미 걸린 것인지, 걸라는 것인지 읽는 쪽이 알 수 없다.
        const done = !!c && c.status === 'punish';
        const tail = '<span class="text-muted">회수예약 = 앞으로 받을 -6차에서 그 개수만큼 차감</span>';

        // 원장에서 grant 를 못 찾아도 부정취득은 그대로다 — 판정한 건수만큼 예약으로 돌린다
        if (!list.length) {
            return total
                ? `<div class="alert alert-warning py-2 mb-2" style="font-size:12.5px">
                       이 케이스로 발급된 <b>-6차를 원장에서 찾지 못했습니다.</b>
                       ${done ? `근거 오더 <b>${total}건</b>만큼 <b>회수예약을 걸었습니다.</b>`
                              : `제재를 등록하면 근거 오더 <b>${total}건</b>만큼 <b>회수예약이 자동으로 걸립니다.</b>`}
                       <br>${tail}
                       ${this.msixLink(c)}</div>`
                : `<div class="alert alert-secondary py-2 mb-2" style="font-size:12.5px">
                       이 케이스에 <b>-6차 근거 오더가 없습니다.</b> 회수할 것이 없어 제재를 등록할 수 없습니다.</div>`;
        }

        const cls = { active: 'is-msix-on', recalled: 'is-msix-off', used: 'is-msix-gone',
                      converted: 'is-msix-gone', canceled: 'is-msix-off' };
        const rows = list.map(m => `
            <div class="co-msix-row ${cls[m.state] || ''}">
                <i class="fas fa-bolt res-i is-msix"></i>
                <a href="javascript:;" class="co-order" data-o="${m.ordernum}">${m.ordernum}</a>
                <span class="text-muted">${m.start_date || ''} 발급</span>
                <b>${this.esc(m.stateTitle || '')}</b>
            </div>`).join('');

        const on      = list.filter(m => m.state === 'active').length;
        const gone    = list.filter(m => m.state === 'used' || m.state === 'converted').length;
        const missing = Math.max(0, total - list.length);

        const guide = [];
        if (done) {
            if (gone)    guide.push(`이미 써버린 <b>${gone}건</b>은 실물이 없어 <b>회수예약을 걸었습니다</b>`);
            if (missing) guide.push(`원장에서 못 찾은 <b>${missing}건</b>도 <b>회수예약을 걸었습니다</b>`);
        } else {
            if (on)      guide.push(`보유중 <b>${on}건</b>은 <b>즉시 회수됩니다</b>`);
            if (gone)    guide.push(`이미 써버린 <b>${gone}건</b>은 실물이 없어 <b>회수예약이 자동으로 걸립니다</b>`);
            if (missing) guide.push(`원장에서 못 찾은 <b>${missing}건</b>도 <b>회수예약이 자동으로 걸립니다</b>`);
        }

        const lead = done ? '처리 결과' : '제재를 등록하면';
        const text = guide.length
            ? `<b>${lead}</b> — ${guide.join(' · ')}${gone + missing ? `<br>${tail}` : ''}`
            : '';

        return `<div class="co-msix">
                    <div class="co-msix-head">회수 대상 -6차 ${total || list.length}건</div>
                    ${rows}
                    ${text ? `<div class="co-msix-guide">${text}</div>` : ''}
                    ${gone + missing ? this.msixLink(c) : ''}
                </div>`;
    }

    /**
     * 「-6차 회수」탭으로 — 그 회원의 보유·예약을 한 판에서 본다.
     *
     * **제재가 등록된 뒤에만 낸다.** 판정 전의 안내문은 "이렇게 처리됩니다" 라는 예고이고
     * 그때는 볼 예약이 아직 없다 — 링크를 따라가 빈 판을 보면 "예약이 안 걸렸나" 로 읽힌다.
     */
    msixLink(c) {
        if (!c || c.status !== 'punish') return '';

        const no = parseInt(c.mb_a);
        if (!no) return '';

        // 회원 지정은 검색칸 규약 그대로 `이름#번호` 다 (# 는 주소에서 조각이 되므로 인코딩)
        const mem = encodeURIComponent(`${(c.a_name || '').trim()}#${no}`);

        return `<div class="co-msix-link">
                    <a href="/adm/member/penalty?tab=msix&mem=${mem}" target="_blank">
                        <i class="fas fa-external-link-alt"></i> 회수예약 확인 — -6차 회수
                    </a>
                </div>`;
    }

    /**
     * 근거 오더를 접수목록에서 한 화면에 나란히 보는 링크.
     * 오더핸들 창은 한 건씩만 열려 두 오더를 비교할 수 없다 — 현장·금액·시간을 맞대보려면
     * 접수목록의 한 표에 두 건이 같이 떠야 한다. 접수의 오더번호칸은 콤마 다건검색을 받는다.
     */

    ordersLink(orders) {
        const nums = (orders || []).map(o => parseInt(o.ordernum)).filter(Boolean);
        if (!nums.length) return '';
        return `<a href="javascript:;" class="co-orders-open ml-2" data-n="${nums.join(',')}">
                    <i class="fas fa-external-link-alt"></i> 접수창에서 보기</a>`;
    }

    bindActions() {
        // **먼저 걷어낸다.** config() 는 화면이 탭을 열 때마다 불린다(배점표를 넘기려고) —
        // 그때마다 위임을 얹으면 버튼 하나에 핸들러가 여러 개 붙어 **확인창이 그 수만큼 뜬다**
        // (등록을 끝냈는데 같은 물음이 또 뜨는 증상). 네임스페이스를 둔 이유가 이것이다.
        $(document).off('.collusion');

        // 모달은 body 밑에 붙으므로 document 위임
        // 회원 이름 = 기사편집 바로(memberCell). 예전엔 제재관리 화면이 걸어줬는데 그 탭이 모아보기로
        // 옮겨가면서 창 쪽이 직접 건다 — 어느 화면에서 열려도 같게
        $(document).on('click.collusion', '.co-member', (e) => {
            e.stopPropagation();
            $j.F.Admin.Util.memberPortal($(e.currentTarget).data('mb'));
        });
        $(document).on('click.collusion', '.co-order', (e) => {
            e.stopPropagation();
            $j.F.Admin.Util.openOrderHandle($(e.currentTarget).data('o'));
        });
        $(document).on('click.collusion', '.co-orders-open', (e) => {
            e.stopPropagation();
            const n = $(e.currentTarget).data('n');
            // 창 이름을 고정해 케이스를 옮겨다녀도 접수창은 한 장만 뜬다
            const w = window.open('/adm/orders?n=' + encodeURIComponent(n), 'adm-orders-search');
            if (w) w.focus();
        });
        $(document).on('click.collusion', '.co-goto', (e) => {
            e.stopPropagation();
            this.open($(e.currentTarget).data('case'));
        });
        $(document).on('click.collusion', '[data-judge]', (e) => {
            this.judge($(e.currentTarget).data('case'), $(e.currentTarget).data('judge'));
        });
        $(document).on('click.collusion', '#coReopen', (e) => {
            this.reopen($(e.currentTarget).data('case'));
        });
        $(document).on('click.collusion', '#coPunish', (e) => {
            this.openPunish($(e.currentTarget).data('case'));
        });
        $(document).on('click.collusion', '#coPunishOk', () => this.punish());
        $(document).on('click.collusion', '#coPunishOne', (e) => {
            this.punishOne($(e.currentTarget).data('case'));
        });
    }

    /**
     * 재발주 제재 — **이 창에서 -6차 회수까지 끝낸다.**
     *
     * 전에는 공용 제재관리 모달을 열어 죄목만 등록하고, 실제 회수는 `/adm/msix` 로 건너가
     * 그 회원의 grant 를 다시 찾아야 했다 (CS 가 케이스 하나에 화면 셋).
     * 회수 대상은 케이스가 이미 알고 있으므로 창을 옮길 이유가 없다.
     *
     * 무엇을 하게 되는지 확인창에 그대로 적는다 — 회수는 되돌릴 수 없다.
     */

    punishOne(caseNum) {
        const memo = ($('#coMemo').val() || '').trim();
        if (!memo) { Util.swalert('제재 사유를 적어주세요.'); $('#coMemo').focus(); return; }

        const cur      = this.current || {};
        const list     = cur.msix || [];
        const total    = parseInt(cur.msixOrders) || 0;
        const on       = list.filter(m => m.state === 'active').length;
        const gone     = list.filter(m => m.state === 'used' || m.state === 'converted').length;
        const recalled = list.filter(m => m.state === 'recalled').length;
        const canceled = list.filter(m => m.state === 'canceled').length;
        // 원장에서 못 찾은 건도 이미 쓴 건과 같다 — 회수할 실물이 없으니 예약으로 돌린다
        const wait     = gone + Math.max(0, total - list.length);

        if (!on && !wait) {
            Util.swalert(recalled ? '이미 회수된 -6차뿐입니다.'
                  : canceled ? '오더취소로 이미 소멸된 -6차뿐입니다.'
                             : '이 케이스에 -6차 근거 오더가 없습니다.');
            return;
        }

        const what = [];
        if (on)   what.push(`보유중 ${on}건 회수`);
        if (wait) what.push(`회수할 실물이 없는 ${wait}건은 회수예약`);

        Util.swalert(`${what.join(' · ')}하고 부정발주 제재로 등록할까요?<br>회수는 되돌릴 수 없습니다.`, () => {
            $.ajax({
                url: `/adm/ajax/collusion/${caseNum}/punish/one`, type: 'post', dataType: 'json',
                data: { memo },
                success: (r) => {
                    if (!r.result) { Util.swalert(r.message || '제재하지 못했습니다.'); return; }
                    Util.swalert(r.message, () => {
                        $j.Ui.JBModal.close('collusion-case-modal');
                        this.changed();
                    });
                },
            });
        }, ['예', '아니오']);
    }

    /**
     * 미판정으로 되돌리기 — **판정을 고치는 유일한 문.**
     *
     * 판정칸은 끝난 건을 읽기 전용으로 두므로(judgeBox), 잘못 판정한 건은 되돌려 미판정으로
     * 만든 다음 다시 판정한다. 되돌리면 원장에도 다시 올라온다 — 담당은 비워진 채로 돌아오므로
     * (AdminTaskService::upsertOpen 의 reopen) 다시 여는 사람이 맡는다.
     *
     * **사유는 지우지 않고 그대로 넘긴다.** 서버는 빈 메모를 null 로 쓰므로 `#coMemo` 가 없는
     * 이 화면에서 judge() 를 그대로 부르면 "무엇을 보고 그렇게 판정했나" 가 사라진다.
     * 되돌린 뒤 다시 연 판정칸에 그 문장이 채워져 있어야 고쳐 쓸 수 있다.
     */
    reopen(caseNum) {
        const memo = $.trim(((this.current || {}).case || {}).memo || '');

        Util.swalert('이 케이스를 미판정으로 되돌릴까요?<br>판정 사유는 그대로 두고 다시 판정할 수 있게 됩니다.', () => {
            $.ajax({
                url: `/adm/ajax/collusion/${caseNum}/judge`, type: 'post', dataType: 'json',
                data: { status: 'new', memo },
                success: (r) => {
                    if (!r.result) { Util.swalert(r.message || '되돌리지 못했습니다.'); return; }
                    Util.swalert(r.message, () => {
                        $j.Ui.JBModal.close('collusion-case-modal');
                        this.changed();
                    });
                },
            });
        }, ['예', '아니오']);
    }

    judge(caseNum, status) {
        const memo = ($('#coMemo').val() || '').trim();
        if (status === 'clear' && !memo) { Util.swalert('정상으로 판정하려면 사유를 적어주세요.'); $('#coMemo').focus(); return; }

        $.ajax({
            url: `/adm/ajax/collusion/${caseNum}/judge`, type: 'post', data: { status, memo },
            success: (r) => {
                if (!r.result) { Util.swalert(r.message || '저장하지 못했습니다.'); return; }
                Util.swalert(r.message, () => {
                    $j.Ui.JBModal.close('collusion-case-modal');
                    this.changed();
                });
            },
        });
    }

    /** 제재 등록창 — 참여자는 케이스 당사자(+같은 패 전원)를 기본으로 채우고 빼고 넣을 수 있다 */

    async openPunish(caseNum) {
        const memo = ($('#coMemo').val() || '').trim();
        try {
            const r = await $.ajax({ url: `/adm/ajax/collusion/${caseNum}/participants`, type: 'get', dataType: 'json' });
            const rows = (r.rows || []).map(m => `
                <label class="co-punish-row">
                    <input type="checkbox" class="co-punish-chk" value="${m.mb_no}" checked>
                    <b>${this.esc(m.mem_name || '')}</b>
                    <span class="text-muted">#${m.mb_no} ${this.esc(m.sido || '')} ${this.esc(m.mem_phone || '')}</span>
                </label>`).join('');

            $j.Ui.JBModal.html('collusion-punish-modal', `
                <div class="co-punish" data-case="${caseNum}">
                    <div class="alert alert-warning py-2" style="font-size:12.5px">
                        죄목 <b>짜고치기</b> · 처벌 <b>수발주제한</b>으로 등록됩니다.
                        선택한 회원들은 <b>서로</b> 발주·수주를 할 수 없게 됩니다(한 패 단위).
                        등록하면 이 화면의 <b>「수발주제한」 탭</b>에 나타나고, 해제도 거기서 합니다.
                    </div>
                    <div class="co-punish-list">${rows || '<div class="text-muted">참여자를 찾지 못했습니다.</div>'}</div>
                    <label class="mt-2 mb-1">제재 사유</label>
                    <textarea class="form-control" id="coPunishMemo" rows="2"
                              placeholder="예) 3개월간 상호 발주·수주 반복, -6차 13건 취득">${this.esc(memo)}</textarea>
                    <div class="mt-2 text-right">
                        <button type="button" class="btn btn-sm btn-danger" id="coPunishOk">
                            <i class="fas fa-ban"></i> 제재 등록
                        </button>
                    </div>
                </div>`, '짜고치기 제재 등록', { width: '560px' });
        } catch (e) {
            Util.swalert('참여자를 불러오지 못했습니다.');
        }
    }

    punish() {
        const $wrap = $('.co-punish');
        const caseNum = $wrap.data('case');
        const mbNos = $wrap.find('.co-punish-chk:checked').map((i, el) => el.value).get();
        const memo = ($('#coPunishMemo').val() || '').trim();

        if (mbNos.length < 2) { Util.swalert('참여자를 2명 이상 선택하세요.'); return; }
        if (!memo) { Util.swalert('제재 사유를 입력하세요.'); $('#coPunishMemo').focus(); return; }

        Util.swalert(`${mbNos.length}명을 짜고치기(수발주제한)로 등록할까요?`, () => {
            $.ajax({
                url: `/adm/ajax/collusion/${caseNum}/punish`, type: 'post', data: { mbNos, memo },
                success: (r) => {
                    if (!r.result) { Util.swalert(r.message || '등록하지 못했습니다.'); return; }
                    Util.swalert(r.message, () => {
                        $j.Ui.JBModal.close('collusion-punish-modal');
                        $j.Ui.JBModal.close('collusion-case-modal');
                        this.changed();
                    });
                },
            });
        }, ['예', '아니오']);
    }

    // ─────────────────────────────────────────────── 조각

    /** 이름 = 기사편집 바로(일부러 둔 동선, Penalty/Collusion 의 .co-member 위임) · #번호 = 회원 팝오버 */
    memberCell(mbNo, name, sido) {
        if (!mbNo) return '<span class="text-muted">—</span>';
        return `<a href="javascript:;" class="co-member" data-mb="${mbNo}">${this.esc(name || '')}</a>
                <span class="text-muted">#${$j.F.Admin.Member.Pop.cellNo(mbNo)}${sido ? ' · ' + this.esc(sido) : ''}</span>`;
    }

    flags(d) {
        const x = d.detail || {};
        const out = [];
        if (d.ring_no > 0) out.push(`<span class="co-flag is-ring">무리</span>`);
        if (x.mutual > 0)  out.push(`<span class="co-flag is-mutual">상호</span>`);
        if (x.special)     out.push(`<span class="co-flag is-special">특차</span>`);
        return out.join(' ') || '';
    }

    statusBadge(d) {
        return `<span class="co-badge is-${d.status}">${this.statusText(d.status)}</span>`;
    }

    /** 목록의 상태칸 — 뱃지 하나. 담당은 **옆 칸**이 말한다(assigneeCell) */
    statusCell(d) {
        return this.statusBadge(d);
    }

    /**
     * 목록의 담당칸 — **상태 바로 오른쪽**이 관리자 화면 공통 자리다(악덕신고·탈퇴와 같다).
     * 한 칸에 상태와 이름을 같이 적었더니 두 가지가 한 낱말처럼 붙어 읽혔다("이재일 제재").
     *
     * 담당은 원장(g5_admin_tasks.assignee)이 정본이다 — 도메인이 자기 표에 따로 적으면
     * 두 곳이 어긋나고, 거기엔 잡기·넘겨받기 손잡이도 없다. 컨트롤러가 원장에서 합쳐 보낸다.
     * 판정이 끝난 건은 **판정한 사람과 그 시각**을 적는다 — 그건 담당이 아니라 업무 사실이다.
     * 이름 위·시각 아래 두 줄은 원장 완료 탭(`Tasks` 처리자칸)과 같은 규칙이다.
     */
    assigneeCell(d) {
        const mine   = $.trim(d.assignee_name || '');
        const judged = $.trim(d.done_name || '');
        const who    = (d.status === 'new') ? mine : (judged || mine);

        if (!who) return '<span class="text-muted">-</span>';

        // 아직 판정 전이면 '지금 보고 있는 사람' 이라 한 단 진하게 (악덕신고와 같은 규칙)
        const live = (d.status === 'new' || d.status === 'check') && mine && !judged;
        // 시각은 판정이 끝난 건만 — 담당은 '지금 보고 있다' 라 시각이 붙을 자리가 아니다.
        // 연도는 버린다(`MM-DD HH:MM`) — 90px 칸이고 판정은 케이스 기간 안에서 일어난다
        const at = judged ? (d.done_at || '').slice(5, 16) : '';

        return `<span class="co-judged${live ? ' is-live' : ''}">${this.esc(who)}</span>`
             + (at ? `<div class="co-judged-at">${this.esc(at)}</div>` : '');
    }

    statusText(s) {
        return (this.cfg && this.cfg.status && this.cfg.status[s])
            // 서버 cfg(CollusionConfig::STATUS)가 정본이고 이건 폴백이다 — 같은 말로 맞춰 둔다
            || { new: '미판정', check: '검토중', clear: '정상', punish: '제재' }[s] || s;
    }

    scoreCls(score) {
        const n = parseInt(score);
        if (n >= 70) return 'is-high';
        if (n >= 50) return 'is-mid';
        if (n >= 40) return 'is-low';
        return 'is-none';
    }

    roleBadge(role) {
        const M = { deal: ['거래', 'is-deal'], src: ['수주한 오더', 'is-src'], dst: ['재발주', 'is-dst'] }[role];
        return M ? `<span class="co-role ${M[1]}">${M[0]}</span>` : role;
    }

    empty(msg) { return `<div class="text-muted text-center p-4">${msg}</div>`; }

    n(v) { return (parseInt(v) || 0).toLocaleString(); }

    esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
